import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { X } from 'lucide-react';
import { AppSheet } from '../common/AppViewKit';
import { useViewMode } from '../../hooks/useViewMode';

// Same rule CustomerDetailPage uses: the app shell, or any phone-width screen.
export function useIsAppView() {
  const { viewMode } = useViewMode();
  const [narrow, setNarrow] = useState(() => window.innerWidth < 640);
  useEffect(() => {
    const on = () => setNarrow(window.innerWidth < 640);
    window.addEventListener('resize', on);
    return () => window.removeEventListener('resize', on);
  }, []);
  return viewMode === 'app' || narrow;
}

export function Switch({ checked, onChange, disabled, label }) {
  return (
    <button type="button" role="switch" aria-checked={!!checked} aria-label={label} disabled={disabled}
      onClick={() => onChange(!checked)}
      style={{
        width: 46, height: 28, borderRadius: 14, border: 'none', padding: 3, flexShrink: 0, cursor: disabled ? 'default' : 'pointer',
        background: checked ? 'var(--primary, #2563eb)' : 'var(--border, #d1d5db)', opacity: disabled ? .5 : 1,
        display: 'flex', justifyContent: checked ? 'flex-end' : 'flex-start', transition: 'background .15s',
      }}>
      <span style={{ width: 22, height: 22, borderRadius: 11, background: '#fff', boxShadow: '0 1px 2px rgba(0,0,0,.3)' }} />
    </button>
  );
}

// Tall sheet that covers the screen below the top, with a sticky footer for the main action.
export function FullSheet({ title, onClose, footer, children }) {
  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 200, display: 'flex', flexDirection: 'column', background: 'var(--bg, #f3f4f6)', color: 'var(--text)' }}>
      <div style={{ display: 'flex', alignItems: 'center', padding: '0 4px 0 14px', minHeight: 52, background: 'var(--bg-card)', borderBottom: '1px solid var(--border)' }}>
        <span style={{ flex: 1, fontWeight: 700, fontSize: '1rem', minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{title}</span>
        <button type="button" onClick={onClose} aria-label="Close"
          style={{ width: 48, height: 48, border: 'none', background: 'none', color: 'var(--text-muted)', display: 'grid', placeItems: 'center', cursor: 'pointer' }}>
          <X size={20} />
        </button>
      </div>
      <div style={{ flex: 1, overflowY: 'auto', padding: 12, WebkitOverflowScrolling: 'touch' }}>{children}</div>
      {footer && (
        <div style={{ padding: '10px 12px calc(10px + env(safe-area-inset-bottom, 0px))', background: 'var(--bg-card)', borderTop: '1px solid var(--border)' }}>{footer}</div>
      )}
    </div>
  );
}

// Confirmation before a sensitive action. `confirm` = { title, body, yes, danger, onYes } or null.
export function ConfirmSheet({ confirm, onClose }) {
  const [busy, setBusy] = useState(false);
  if (!confirm) return null;
  const go = async () => {
    setBusy(true);
    try { await confirm.onYes(); } finally { setBusy(false); onClose(); }
  };
  return (
    <div style={{ position: 'relative', zIndex: 300 }}>
      <AppSheet title={confirm.title} onClose={onClose}>
        <div style={{ fontSize: '.9rem', lineHeight: 1.45, color: 'var(--text)', margin: '4px 0 16px', whiteSpace: 'pre-line' }}>{confirm.body}</div>
        <div style={{ display: 'grid', gap: 8 }}>
          <button type="button" className={`btn ${confirm.danger ? 'btn-danger' : 'btn-primary'}`} style={{ minHeight: 48 }} disabled={busy} onClick={go}>
            {busy ? 'Please wait…' : confirm.yes || 'Confirm'}
          </button>
          <button type="button" className="btn btn-secondary" style={{ minHeight: 48 }} onClick={onClose}>Cancel</button>
        </div>
      </AppSheet>
    </div>
  );
}

export function Chip({ active, onClick, children }) {
  return (
    <button type="button" onClick={onClick}
      style={{
        minHeight: 36, padding: '0 14px', borderRadius: 18, whiteSpace: 'nowrap', fontSize: '.82rem', fontWeight: 600, cursor: 'pointer',
        border: `1px solid ${active ? 'var(--primary, #2563eb)' : 'var(--border)'}`,
        background: active ? 'var(--primary, #2563eb)' : 'var(--bg-card)', color: active ? '#fff' : 'var(--text)',
      }}>{children}</button>
  );
}

// Users | Roles | Activity. Roles is its own page, Activity is a tab on the Users page.
export function UsersTabs({ active }) {
  const navigate = useNavigate();
  const tabs = [
    { key: 'users', label: 'Users', to: '/users' },
    { key: 'roles', label: 'Roles', to: '/site-roles' },
    { key: 'activity', label: 'Activity', to: '/users?tab=activity' },
  ];
  return (
    <div role="tablist" style={{ display: 'flex', background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 10, padding: 3, marginBottom: 12 }}>
      {tabs.map(t => (
        <button key={t.key} type="button" role="tab" aria-selected={active === t.key} onClick={() => active !== t.key && navigate(t.to)}
          style={{
            flex: 1, minHeight: 40, border: 'none', borderRadius: 8, fontWeight: 700, fontSize: '.86rem', cursor: 'pointer',
            background: active === t.key ? 'var(--primary, #2563eb)' : 'transparent', color: active === t.key ? '#fff' : 'var(--text-muted)',
          }}>{t.label}</button>
      ))}
    </div>
  );
}

export const fabStyle = {
  position: 'fixed', right: 16, bottom: 'calc(74px + env(safe-area-inset-bottom, 0px))', zIndex: 40,
  width: 56, height: 56, borderRadius: 28, border: 'none', background: 'var(--primary, #2563eb)', color: '#fff',
  display: 'grid', placeItems: 'center', boxShadow: '0 4px 12px rgba(0,0,0,.3)', cursor: 'pointer',
};

export const sectionLabel = {
  fontSize: '.72rem', fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '.05em', margin: '14px 4px 6px',
};
export const groupBox = { background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 12, overflow: 'hidden' };
